import React, { useState, useEffect, useMemo } from "react";
import { Alert, Box, Button } from "@mui/material";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { useApiClient } from "../../../api";
import { Publish } from "@mui/icons-material";
import { QuestionViewCard } from "../../question/components/QuestionViewCard";
import { enqueueSnackbar } from "notistack";
import { useCheckForExistingAnswers } from "../hooks/useCheckForExistingAnswers";

export const StudentQuizSubmitter = ({
  quizQuestions,
  onOptionsSelected,
  studentsAnswers,
  showMarks = false,
}) => {
  return (
    <>
      {quizQuestions?.map((qq) => (
        <QuestionViewCard
          key={qq.id}
          question={qq.question}
          sx={{ mt: 2 }}
          hideMarkScheme
          quizSubmission
          onChosenOptionsChange={(options) => onOptionsSelected(qq.id, options)} // TODO
          chosenOptions={studentsAnswers[qq.id]}
          showMarks={showMarks}
        />
      ))}
    </>
  );
};

export const QuizSubmission = ({
  identifier = null,
  quizGroup,
  setIdentifier = null,
  tokenView = false,
  teacherView = false,
}) => {
  const ApiClient = useApiClient();

  const [quizQuestions, setQuizQuestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [studentsAnswers, setStudentsAnswers] = useState({});
  const [existingAnswers, setExistingAnswers] = useState({});
  const [disableChangeResponses, setDisableChangeResponses] = useState(false);
  const [showMarks, setShowMarks] = useState(false);
  const [selectedQuizId, setSelectedQuizId] = useState(null);

  const { checkForExistingAnswers, error } = useCheckForExistingAnswers({
    quizGroup,
    setQuizQuestions,
    setStudentsAnswers,
    setExistingAnswers,
    setShowMarks,
    setDisableChangeResponses,
    setSelectedQuizId,
    teacherView,
  });

  useEffect(() => {
    if (tokenView && !identifier?.id) return;
    checkForExistingAnswers(identifier);
  }, [identifier, tokenView]);

  const handleSubmit = async () => {
    setSubmitting(true); // TODO
    try {
      const response = await ApiClient.quiz.submitQuizResponse({
        identifier: identifier?.id,
        quiz: selectedQuizId,
        payload: JSON.stringify(studentsAnswers),
      });
      if (response.success) {
        enqueueSnackbar(response.message, {
          variant: "success",
          autoHideDuration: 3000,
        });
        if (tokenView) {
          setIdentifier({});
          setExistingAnswers({});
          setStudentsAnswers({});
          setQuizQuestions([]);
        }
      } else {
        enqueueSnackbar("There was a problem submitting, please try again", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Error submitting answers", error);
      enqueueSnackbar("There was a problem submitting, please try again", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleStudentSelectsOptions = (quizQuestionId, selectedOptions) => {
    if (disableChangeResponses) {
      // TODO: better disabling?
      return;
    }
    setStudentsAnswers((prev) => ({
      ...prev,
      [quizQuestionId]: selectedOptions,
    }));
  };

  const normaliseAnswers = (answers = {}) =>
    Object.fromEntries(
      Object.entries(answers)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([questionId, optionIds]) => [questionId, [...optionIds].sort()]),
    );

  const answersUnchanged = useMemo(() => {
    if (!existingAnswers || !studentsAnswers) return false;

    return (
      JSON.stringify(normaliseAnswers(existingAnswers)) ===
      JSON.stringify(normaliseAnswers(studentsAnswers))
    );
  }, [existingAnswers, studentsAnswers]);

  const allQuestionsAnswered =
    quizQuestions.length > 0 &&
    quizQuestions.every(
      (qq) => studentsAnswers[qq.id] && studentsAnswers[qq.id].length > 0,
    );
  const submitDisabled =
    (identifier && Object.keys(identifier).length === 0) ||
    submitting ||
    !allQuestionsAnswered ||
    answersUnchanged;
  const noEligibleQuiz = Boolean(error) || !selectedQuizId;

  // TODO: functionality to check if the student can resubmit / see marks - perhaps from quizGroup itself?
  return (
    <FruitifyContainer p={2}>
      <StudentQuizSubmitter
        quizQuestions={quizQuestions}
        onOptionsSelected={handleStudentSelectsOptions}
        studentsAnswers={studentsAnswers}
        showMarks={showMarks}
      />
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error.message}
        </Alert>
      )}
      {!disableChangeResponses && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Button
            disabled={submitDisabled || noEligibleQuiz}
            variant={"outlined"}
            endIcon={<Publish />}
            fullWidth
            onClick={() => handleSubmit()}
          >
            Submit
          </Button>
        </Box>
      )}
    </FruitifyContainer>
  );
};
